<script setup lang="ts">
	import type { ApiDeliveriesResult } from '~/types';

	const triggers = useYmTriggers();
	const calcStorage = useCalculatorStorage();

	const props = defineProps<{ otherPrice?: ApiDeliveriesResult }>();

	const options = computed(() => {
		const calcValues = calcStorage.values;

		const railUrl = new URL(`${CONTACTS.railTransportation.link}/#calculator`);
		railUrl.searchParams.set('values', JSON.stringify(calcValues.value));

		return [
			{
				image: '/images/alt1.png',
				alt: 'Грузовик едет из Китая',
				text: CONTACTS.autoTransportation.text,
				link: CONTACTS.autoTransportation.link,
				price: props.otherPrice?.auto,
				targetIdYm: 'go_to_auto',
			},
			{
				image: '/images/alt2.png',
				alt: 'Поезд везет товары из Китая',
				text: CONTACTS.railTransportation.text,
				link: railUrl.toString(),
				price: props.otherPrice?.train,
				targetIdYm: 'go_to_train',
			},
		];
	});

	function handleLinkClick(targetId: string) {
		triggers.reachGoal(targetId);
	}
</script>

<template>
	<section class="alternative-section">
		<h2 class="alternative-section__title">
			Не подходит авиаперевозка?
			<br />
			У нас есть другие решения!
		</h2>

		<div class="alternative-section__body">
			<div v-for="item in options" :key="item.image" class="alternative-section__card">
				<NuxtImg
					:src="item.image"
					:alt="item.alt"
					densities="x1 x2"
					format="webp"
					sizes="360px xs:600px md:1200px xl:588px"
					loading="lazy"
					class="card__image"
				/>
				<div class="card__name">{{ item.text }}</div>
				<div
					v-if="Number(item.price?.result_cny) && Number(item.price?.result_rub)"
					class="card__price"
				>
					<div class="card__price-key">Стоимость доставки:</div>
					<div class="card__price-value">
						{{
							new Intl.NumberFormat('ru-RU', {
								style: 'currency',
								currency: 'jpy',
							}).format(Number(item.price?.result_cny))
						}}
						, ({{
							new Intl.NumberFormat('ru-RU', {
								style: 'currency',
								currency: 'rub',
							}).format(Number(item.price?.result_rub))
						}})
					</div>
				</div>
				<a
					:href="item.link"
					target="_blank"
					rel="noopener"
					class="card__link button"
					@click="handleLinkClick(item.targetIdYm)"
				>
					Перейти
					<Icon name="local:arrow-up-right" aria-hidden="true" />
				</a>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
	.alternative-section__title {
		font-size: var(--font-size-title);
		font-weight: 700;
		color: var(--color-tertiary);
		margin-bottom: 15px;
	}
	.alternative-section__body {
		display: grid;
		grid-template-columns: 1fr;
		gap: 20px;
	}
	.alternative-section__card {
		width: 100%;
		display: grid;
		gap: 12px;
	}
	.card__image {
		display: flex;
		width: 100%;
		height: auto;
		aspect-ratio: 2.67;
		border-radius: 20px;
		object-fit: cover;
	}
	.card__name {
		font-size: 24px;
		font-weight: 600;
	}
	.card__price {
		display: flex;
		flex-wrap: wrap;
		gap: 0px 8px;
		color: var(--color-grey-dark);
		margin-top: -8px;
	}
	.card__price-key {
		font-weight: 700;
	}
	.card__link {
		width: 100%;
		color: var(--color-white);
	}

	@media (min-width: 1240px) {
		.alternative-section__title {
			margin-bottom: 25px;
		}
		.alternative-section__body {
			grid-template-columns: 1fr 1fr;
			gap: 24px;
		}
		.alternative-section__card {
			gap: 15px;
		}
		.card__image {
			width: 100%;
			height: 220px;
			aspect-ratio: unset;
			border-radius: 50px;
			object-fit: cover;
		}
		.card__name {
			font-size: 30px;
		}
	}
</style>
